import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Loader2, X } from "lucide-react";
import { listAllTestimonials, upsertTestimonial, deleteTestimonial } from "@/lib/testimonials.functions";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const listQ = () => queryOptions({ queryKey: ["admin", "testimonials"], queryFn: () => listAllTestimonials() });

export const Route = createFileRoute("/_authenticated/admin/testimonials")({
  loader: ({ context }) => context.queryClient.ensureQueryData(listQ()),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: TestimonialsAdmin,
});

type Row = {
  id?: string;
  author_name: string;
  author_role: string | null;
  author_city: string | null;
  avatar_url: string | null;
  quote_ar: string;
  rating: number | null;
  is_featured: boolean;
  sort_order: number;
  is_published: boolean;
};

function blank(): Row {
  return {
    author_name: "",
    author_role: "",
    author_city: "",
    avatar_url: null,
    quote_ar: "",
    rating: 5,
    is_featured: false,
    sort_order: 0,
    is_published: true,
  };
}

function TestimonialsAdmin() {
  const { data } = useSuspenseQuery(listQ());
  const [editing, setEditing] = useState<Row | null>(null);
  const qc = useQueryClient();
  const del = useServerFn(deleteTestimonial);

  async function remove(id: string) {
    if (!window.confirm("حذف هذه الشهادة نهائياً؟")) return;
    try {
      await del({ data: { id } });
      toast.success("تم الحذف");
      qc.invalidateQueries({ queryKey: ["admin", "testimonials"] });
      qc.invalidateQueries({ queryKey: ["public", "testimonials"] });
    } catch (e: any) {
      toast.error(e?.message ?? "فشل الحذف");
    }
  }

  return (
    <div>
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="kicker text-modern-orange">إدارة المحتوى</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-foreground">الشهادات</h1>
        </div>
        <button
          onClick={() => setEditing(blank())}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" /> أضف شهادة
        </button>
      </header>

      {data.length === 0 ? (
        <div className="rounded-ds-lg border border-dashed border-border bg-card/50 p-12 text-center">
          <p className="text-muted-foreground">لا شهادات بعد.</p>
          <button
            onClick={() => setEditing(blank())}
            className="mt-4 inline-flex rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover"
          >
            أضف أول شهادة
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.map((t: any) => (
            <article key={t.id} className="rounded-2xl bg-card p-5 shadow-soft">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-base font-bold text-foreground">{t.author_name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {[t.author_role, t.author_city].filter(Boolean).join(" — ")}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-modern-orange">
                  {t.is_featured && <Star className="h-4 w-4 fill-current" aria-label="مميّزة" />}
                  {!t.is_published && <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">مسودة</span>}
                </div>
              </div>
              <p className="mt-3 line-clamp-3 text-sm leading-loose text-muted-foreground">«{t.quote_ar}»</p>
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => setEditing(t)}
                  className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1.5 text-xs font-bold text-foreground hover:bg-accent/15"
                >
                  <Pencil className="h-3 w-3" /> عدّل
                </button>
                <button
                  onClick={() => remove(t.id)}
                  className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-background px-3 py-1.5 text-xs font-bold text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3 w-3" /> احذف
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && <Editor row={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Editor({ row, onClose }: { row: Row; onClose: () => void }) {
  const [form, setForm] = useState<Row>(row);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const save = useServerFn(upsertTestimonial);

  async function submit() {
    if (!form.author_name.trim() || !form.quote_ar.trim()) {
      toast.error("الرجاء تعبئة الاسم والاقتباس.");
      return;
    }
    setBusy(true);
    try {
      await save({
        data: {
          id: form.id,
          author_name: form.author_name.trim(),
          author_role: form.author_role?.trim() || null,
          author_city: form.author_city?.trim() || null,
          avatar_url: form.avatar_url || null,
          quote_ar: form.quote_ar.trim(),
          rating: form.rating ?? null,
          is_featured: form.is_featured,
          sort_order: form.sort_order,
          is_published: form.is_published,
        },
      });
      toast.success("تم الحفظ");
      qc.invalidateQueries({ queryKey: ["admin", "testimonials"] });
      qc.invalidateQueries({ queryKey: ["public", "testimonials"] });
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "فشل الحفظ");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-foreground/30 backdrop-blur-sm" />
      <aside onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-xl overflow-y-auto bg-card p-6 shadow-lift">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold text-foreground">
            {form.id ? "تعديل الشهادة" : "شهادة جديدة"}
          </h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-accent/20" aria-label="إغلاق">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-6 space-y-4">
          <Field label="الاسم" required>
            <input value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className={inputCls} />
          </Field>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="الصفة (مثال: أم لطفلين)">
              <input value={form.author_role ?? ""} onChange={(e) => setForm({ ...form, author_role: e.target.value })} className={inputCls} />
            </Field>
            <Field label="المدينة">
              <input value={form.author_city ?? ""} onChange={(e) => setForm({ ...form, author_city: e.target.value })} className={inputCls} />
            </Field>
          </div>
          <Field label="الاقتباس" required>
            <textarea value={form.quote_ar} onChange={(e) => setForm({ ...form, quote_ar: e.target.value })} rows={4} className={inputCls} />
          </Field>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="التقييم (1-5)">
              <input
                type="number"
                min={1}
                max={5}
                value={form.rating ?? ""}
                onChange={(e) => setForm({ ...form, rating: e.target.value ? Number(e.target.value) : null })}
                className={inputCls}
              />
            </Field>
            <Field label="الترتيب">
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                className={inputCls}
              />
            </Field>
            <Field label="رابط الصورة">
              <input value={form.avatar_url ?? ""} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://..." className={inputCls} />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
              <span>مميّزة</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
              <span>منشورة</span>
            </label>
          </div>
          <div className="pt-4">
            <button
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ الشهادة
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30";

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-semibold text-foreground">
        {label}
        {required && <span className="text-modern-orange"> *</span>}
      </span>
      {children}
    </label>
  );
}